import React, { useState } from 'react';
import {
  Alert,
  Box,
  IconButton,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
  Menu
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  MarkEmailRead,
  MarkEmailUnread,
  OpenInNew,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import api from '../../services/api';
import AssigneeDropdown from '../../utils/AssigneeDropdown';
import { useWorkspace } from '../../utils/WorkspaceContext';
import { SLOT_LABELS } from '../../utils/sprintSlots';
import useTaskListPatcher from '../../hooks/useTaskListPatcher';
import { getStatusColor, getStatusMeta, StatusBadge } from '../../utils/StatusChip';
import { CATEGORY_OPTIONS, CLASS_OPTIONS } from '../../utils/constants';
import { useTaskOverlay, taskHighlightSx } from '../../utils/TaskOverlayContext';
import TaskCreate, { duplicatePrefillFromTask } from './TaskCreate';

// One date format used app-wide.
const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * @typedef {Object} Task
 * @property {number} id - Task ID
 * @property {string} name - Task name
 * @property {string} status - Task status
 * @property {string} do_date - Task due date
 * @property {number} line_up - Task priority
 * @property {boolean} read - Whether task is read
 * @property {Object} assignee - Assigned user
 * @property {Object} reviewer - Reviewer user
 * @property {string} project - Project name
 * @property {string} epic - Epic name
 * @property {string} track - Track name
 * @property {string} sprint - Sprint name
 * @property {number} estimated_time - Estimated time in hours
 * @property {number} total_time_taken - Total time taken in hours
 * @property {number} action_items_count - Total action items
 * @property {number} completed_action_items_count - Completed action items
 */

/**
 * @typedef {Object} VisibleColumns
 * @property {boolean} actions - Show actions column
 * @property {boolean} assignee - Show assignee column
 * @property {boolean} task - Show task name column
 * @property {boolean} status - Show status column
 * @property {boolean} do_date - Show due date column
 * @property {boolean} reviewer - Show reviewer column
 * @property {boolean} line_up - Show priority column
 * @property {boolean} project - Show project column
 * @property {boolean} epic - Show epic column
 * @property {boolean} track - Show track column
 * @property {boolean} sprint - Show sprint column
 * @property {boolean} estimated_time - Show estimated time column
 * @property {boolean} total_time_taken - Show time taken column
 * @property {boolean} ai_progress - Show AI progress column
 */

/**
 * @typedef {Object} ExtendedTaskListProps
 * @property {Task[]} tasks - Array of tasks to display
 * @property {function} fetchTasks - Function to fetch tasks
 * @property {function} setTasks - Function to update tasks state
 * @property {VisibleColumns} visibleCols - Object defining which columns to show
 */

const HeaderRow = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  fontWeight: 'bold',
  fontSize: 12,
  width: '100%',
  flexWrap: 'nowrap',
}));

const TaskRow = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(0.25, 1),
  minHeight: 38,
  alignItems: 'center',
  width: '100%',
  flexWrap: 'nowrap',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    cursor: 'pointer',
  },
  '& input, & select, & button, & .MuiSelect-root, & .status-dropdown': {
    cursor: 'text',
  },
  '& button, & .MuiSelect-root, & .status-dropdown': {
    cursor: 'default',
  },
}));

// Minimal, low-weight trigger that displays a StatusBadge via renderValue.
const StatusSelect = styled(Select)(() => ({
  '& .MuiSelect-select': {
    padding: '2px 24px 2px 4px !important',
    display: 'flex',
    alignItems: 'center',
  },
  '& fieldset': { border: 'none' },
  '&:before, &:after': { display: 'none' },
  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: 6 },
}));

const ColorBar = styled(Box)(({ color }) => ({
  width: '4px',
  height: '100%',
  backgroundColor: color,
  borderRadius: '2px',
  marginRight: '8px',
}));

const IconGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
  justifyContent: 'flex-start',
}));

const SortableHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  padding: theme.spacing(0.5),
  borderRadius: theme.spacing(0.5),
}));

const colWidths = {
  actions: 90,
  assignee: 110,
  task: 260,
  notes: 200,
  category: 160,
  task_class: 120,
  status: 140,
  do_date: 140,
  created_at: 140,
  updated_at: 140,
  read: 60,
  reviewer: 110,
  line_up: 70,
  project: 160,
  epic: 160,
  primary_epic: 160,
  track: 160,
  sprint: 180,
  sprint_track_name: 180,
  is_archived: 90,
  estimated_time: 90,
  total_time_taken: 100,
  action_items_count: 90,
  completed_action_items_count: 90,
  ai_progress: 140,
};

/**
 * Extended task list component with sortable columns and inline editing
 * @component
 * @param {ExtendedTaskListProps} props - Component props
 * @returns {React.ReactElement} The extended task list
 */
const ExtendedTaskList = ({ tasks = [], fetchTasks, setTasks, visibleCols = {} }) => {
  const { currentWorkspace } = useWorkspace();
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  // Duplicate Task opens the create dialog seeded from the selected task.
  const [duplicatePrefill, setDuplicatePrefill] = useState(null);
  const { openTaskDetail, highlightTaskId } = useTaskOverlay();

  /**
   * Debounced function to update task data
   * @param {number} taskId - Task ID to update
   * @param {Object} updates - Object containing fields to update
   */
  // Batched per task, so editing two rows in quick succession saves both.
  // On success there is deliberately no refetch: the optimistic update already
  // landed, and replacing the whole list a second after every keystroke
  // overwrote the cell the user was still typing in with the server's older
  // value.
  const debouncedUpdateTask = useTaskListPatcher({
    workspaceId: currentWorkspace?.id,
    onError: async () => {
      const refreshedTasks = await fetchTasks();
      if (Array.isArray(refreshedTasks)) setTasks(refreshedTasks);
    },
  });

  /**
   * Handles task field updates with optimistic UI updates
   * @param {number} taskId - Task ID to update
   * @param {string} field - Field name to update
   * @param {*} value - New field value
   */
  const handleTaskUpdate = (taskId, field, value) => {
    setTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.id === taskId ? { ...task, [field]: value } : task
      )
    );
    debouncedUpdateTask(taskId, { [field]: value });
  };

  const statusOptions = [
    { value: 'To Start', label: 'To Start' },
    { value: 'Doing', label: 'Doing' },
    { value: 'On hold', label: 'On hold' },
    { value: 'Task Complete', label: 'Task Complete' },
    { value: 'Documented', label: 'Documented' },
    { value: 'Closed w Review', label: 'Closed w Review' },
  ];

  /**
   * Handles status change for a task
   * @param {React.ChangeEvent} e - Change event
   * @param {number} taskId - Task ID to update
   */
  const handleStatusChange = (e, taskId) => {
    e.stopPropagation();
    if (taskId) {
      // Send the status verbatim: Task.STATUS_CHOICES is title-cased, so a
      // lower-cased value fails validation and the PATCH silently 400s.
      handleTaskUpdate(taskId, 'status', e.target.value);
    }
  };

  /**
   * Toggles the read status of a task
   * @param {React.MouseEvent} e - Click event
   * @param {Task} task - Task to toggle
   */
  const handleReadToggle = (e, task) => {
    e.stopPropagation();
    if (task.id) {
      handleTaskUpdate(task.id, 'read', !task.read);
    }
  };

  /**
   * Handles row click to navigate to task details
   * @param {React.MouseEvent} e - Click event
   * @param {number} taskId - Task ID to navigate to
   */
  const handleRowClick = (e, taskId) => {
    if (e.target.closest('input, select, button, .MuiSelect-root, .status-dropdown, [role="option"]')) {
      return;
    }
    openTaskDetail(taskId);
  };

  /**
   * Handles assignee change for a task
   * @param {number} taskId - Task ID to update
   * @param {number} userId - New assignee user ID
   */
  const handleAssigneeChange = (taskId, userId) => {
    if (taskId) {
      handleTaskUpdate(taskId, 'assignee', userId);
    }
  };

  /**
   * Handles reviewer change for a task
   * @param {number} taskId - Task ID to update
   * @param {number} userId - New reviewer user ID
   */
  const handleReviewerChange = (taskId, userId) => {
    if (taskId) {
      handleTaskUpdate(taskId, 'reviewer', userId);
    }
  };

  /**
   * Handles sorting by column
   * @param {string} columnKey - Column key to sort by
   */
  const handleSort = (columnKey) => {
    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });

    const sortedTasks = [...tasks].sort((a, b) => {
      let aValue = a[columnKey];
      let bValue = b[columnKey];

      if (columnKey === 'do_date' || columnKey === 'created_at') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      } else if (columnKey === 'estimated_time' || columnKey === 'total_time_taken' || columnKey === 'line_up') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      } else if (columnKey === 'assignee') {
        aValue = aValue?.first_name || aValue?.username || '';
        bValue = bValue?.first_name || bValue?.username || '';
      } else if (columnKey === 'reviewer') {
        aValue = aValue?.first_name || aValue?.username || '';
        bValue = bValue?.first_name || bValue?.username || '';
      } else if (columnKey === 'status') {
        const statusOrder = {
          'to start': 1,
          'doing': 2,
          'on hold': 3,
          'task complete': 4,
          'documented': 5,
          'closed w review': 6
        };
        aValue = statusOrder[aValue?.toLowerCase()] || 999;
        bValue = statusOrder[bValue?.toLowerCase()] || 999;
      } else {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setTasks(sortedTasks);
  };

  /**
   * Renders a sortable column header
   * @param {string} label - Column label
   * @param {string} columnKey - Column key for sorting
   * @returns {React.ReactElement} Sortable header element
   */
  const renderSortableHeader = (label, columnKey) => (
    <SortableHeader onClick={() => handleSort(columnKey)}>
      <Typography noWrap sx={{ flexGrow: 1 }}>{label}</Typography>
      {sortConfig.key === columnKey && (
        sortConfig.direction === 'asc' ? 
          <ArrowUpward fontSize="small" /> : 
          <ArrowDownward fontSize="small" />
      )}
    </SortableHeader>
  );

  /**
   * Handles right-click context menu for tasks
   * @param {React.MouseEvent} event - Context menu event
   * @param {Task} task - Task to show context menu for
   */
  const handleTaskContextMenu = (event, task) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
    setSelectedTask(task);
  };

  /**
   * Closes the context menu
   */
  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedTask(null);
  };

  /**
   * Deletes a task
   * @async
   */
  const handleDeleteTask = async () => {
    if (!selectedTask?.id) return;
    try {
      const response = await api.delete(`tasks/${selectedTask.id}/`, {
        params: { workspace_id: currentWorkspace.id },
      });
      if (response.status === 204) {
        setTasks((prevTasks) => prevTasks.filter((task) => task.id !== selectedTask.id));
      } else {
        alert('Task delete failed');
        const refreshedTasks = await fetchTasks();
        setTasks(refreshedTasks);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Task delete failed');
      const refreshedTasks = await fetchTasks();
      setTasks(refreshedTasks);
    }
    handleCloseContextMenu();
  };

  /**
   * Opens the create dialog carrying every field of the selected task except
   * its name, notes and action items, which the user fills in fresh.
   */
  const handleDuplicateTask = () => {
    if (!selectedTask) return;
    setDuplicatePrefill(duplicatePrefillFromTask(selectedTask));
    handleCloseContextMenu();
  };

  const duplicateDialog = (
    <TaskCreate
      open={Boolean(duplicatePrefill)}
      prefill={duplicatePrefill}
      onClose={() => setDuplicatePrefill(null)}
      onTaskCreated={async () => {
        const refreshedTasks = await fetchTasks();
        setTasks(refreshedTasks);
      }}
    />
  );

  if (!Array.isArray(tasks) || tasks.length === 0) {
    return (
      <>
        <Alert severity="info">No tasks available for the selected filters.</Alert>
        {duplicateDialog}
      </>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={(theme) => ({
          width: '100%',
          overflowX: 'auto',
          // Pin vertical to hidden so the browser doesn't coerce overflow-y to
          // auto (which it does when overflow-x is auto) and show a stray bar.
          overflowY: 'hidden',
          // Slim, theme-coloured horizontal scrollbar.
          scrollbarWidth: 'thin',
          scrollbarColor: `${theme.palette.divider} transparent`,
          '&::-webkit-scrollbar': { height: 8 },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.divider,
            borderRadius: 8,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: theme.palette.action.active,
          },
        })}
      >
        <Box sx={{ minWidth: 900 }}>
          <HeaderRow container spacing={1} wrap="nowrap" sx={{ columnGap: 1 }}>
            <Box sx={{ width: '4px', marginRight: '8px', height: '100%' }}></Box>
            {visibleCols.actions && <Grid sx={{ flex: `0 0 ${colWidths.actions}px` }} alignSelf="center"><Typography noWrap>Actions</Typography></Grid>}
            {visibleCols.assignee && <Grid sx={{ flex: `0 0 ${colWidths.assignee}px` }} alignSelf="center">{renderSortableHeader('Assignee', 'assignee')}</Grid>}
            {visibleCols.task && <Grid sx={{ flex: `0 0 ${colWidths.task}px` }} alignSelf="center">{renderSortableHeader('Task', 'name')}</Grid>}
            {visibleCols.notes && <Grid sx={{ flex: `0 0 ${colWidths.notes}px` }} alignSelf="center">{renderSortableHeader('Notes', 'notes')}</Grid>}
            {visibleCols.category && <Grid sx={{ flex: `0 0 ${colWidths.category}px` }} alignSelf="center">{renderSortableHeader('Category', 'category')}</Grid>}
            {visibleCols.task_class && <Grid sx={{ flex: `0 0 ${colWidths.task_class}px` }} alignSelf="center">{renderSortableHeader('Class', 'task_class')}</Grid>}
            {visibleCols.status && <Grid sx={{ flex: `0 0 ${colWidths.status}px` }} alignSelf="center">{renderSortableHeader('Status', 'status')}</Grid>}
            {visibleCols.do_date && <Grid sx={{ flex: `0 0 ${colWidths.do_date}px` }} alignSelf="center">{renderSortableHeader('Do Date', 'do_date')}</Grid>}
            {visibleCols.created_at && <Grid sx={{ flex: `0 0 ${colWidths.created_at}px` }} alignSelf="center">{renderSortableHeader('Created', 'created_at')}</Grid>}
            {visibleCols.updated_at && <Grid sx={{ flex: `0 0 ${colWidths.updated_at}px` }} alignSelf="center">{renderSortableHeader('Updated', 'updated_at')}</Grid>}
            {visibleCols.read && <Grid sx={{ flex: `0 0 ${colWidths.read}px` }} alignSelf="center">{renderSortableHeader('Read', 'read')}</Grid>}
            {visibleCols.is_archived && <Grid sx={{ flex: `0 0 ${colWidths.is_archived}px` }} alignSelf="center">{renderSortableHeader('Archived', 'is_archived')}</Grid>}
            {visibleCols.reviewer && <Grid sx={{ flex: `0 0 ${colWidths.reviewer}px` }} alignSelf="center">{renderSortableHeader('Reviewer', 'reviewer')}</Grid>}
            {visibleCols.line_up && <Grid sx={{ flex: `0 0 ${colWidths.line_up}px` }} alignSelf="center">{renderSortableHeader('Line Up', 'line_up')}</Grid>}
            {visibleCols.project && <Grid sx={{ flex: `0 0 ${colWidths.project}px` }} alignSelf="center">{renderSortableHeader('Project', 'project')}</Grid>}
            {visibleCols.epic && <Grid sx={{ flex: `0 0 ${colWidths.epic}px` }} alignSelf="center">{renderSortableHeader('Epic', 'epic')}</Grid>}
            {visibleCols.primary_epic && <Grid sx={{ flex: `0 0 ${colWidths.primary_epic}px` }} alignSelf="center">{renderSortableHeader('Pri. Epic', 'primary_epic')}</Grid>}
            {visibleCols.track && <Grid sx={{ flex: `0 0 ${colWidths.track}px` }} alignSelf="center">{renderSortableHeader('Track', 'track')}</Grid>}
            {visibleCols.sprint && <Grid sx={{ flex: `0 0 ${colWidths.sprint}px` }} alignSelf="center">{renderSortableHeader('Sprint', 'sprint')}</Grid>}
            {visibleCols.sprint_track_name && <Grid sx={{ flex: `0 0 ${colWidths.sprint_track_name}px` }} alignSelf="center">{renderSortableHeader('Sprint Track', 'sprint_track_name')}</Grid>}
            {visibleCols.estimated_time && <Grid sx={{ flex: `0 0 ${colWidths.estimated_time}px` }} alignSelf="center">{renderSortableHeader('Est (h)', 'estimated_time')}</Grid>}
            {visibleCols.total_time_taken && <Grid sx={{ flex: `0 0 ${colWidths.total_time_taken}px` }} alignSelf="center">{renderSortableHeader('Taken (h)', 'total_time_taken')}</Grid>}
            {visibleCols.action_items_count && <Grid sx={{ flex: `0 0 ${colWidths.action_items_count}px` }} alignSelf="center">{renderSortableHeader('AI Count', 'action_items_count')}</Grid>}
            {visibleCols.completed_action_items_count && <Grid sx={{ flex: `0 0 ${colWidths.completed_action_items_count}px` }} alignSelf="center">{renderSortableHeader('AI Done', 'completed_action_items_count')}</Grid>}
            {visibleCols.ai_progress && <Grid sx={{ flex: `0 0 ${colWidths.ai_progress}px` }} alignSelf="center"><Typography noWrap>AI Progress</Typography></Grid>}
          </HeaderRow>

          {tasks.map((task, index) => {
            const est = Number(task.estimated_time || 0);
            const taken = Number(task.total_time_taken || 0);
            const aiTotal = task.action_items_count || 0;
            const aiDone = task.completed_action_items_count || 0;
            const aiPct = aiTotal > 0 ? Math.round((aiDone / aiTotal) * 100) : 0;
            return (
              <TaskRow
                container
                spacing={1}
                wrap="nowrap"
                key={task.id || `task-${index}`}
                onClick={(e) => handleRowClick(e, task.id)}
                onContextMenu={(e) => handleTaskContextMenu(e, task)}
                sx={task.id === highlightTaskId ? taskHighlightSx : undefined}
              >
                <ColorBar color={getStatusColor(task.status?.toLowerCase() || 'default').bg} />
                {visibleCols.actions && (
                  <Grid sx={{ flex: `0 0 ${colWidths.actions}px` }}>
                    <IconGroup>
                      <IconButton
                        size="small"
                        onClick={(e) => handleReadToggle(e, task)}
                        color={task.read ? 'default' : 'secondary'}
                      >
                        {task.read ? <MarkEmailRead fontSize="small" /> : <MarkEmailUnread fontSize="small" />}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openTaskDetail(task.id);
                        }}
                        color="default"
                      >
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </IconGroup>
                  </Grid>
                )}
                {visibleCols.assignee && (
                  <Grid sx={{ flex: `0 0 ${colWidths.assignee}px` }}>
                    <AssigneeDropdown
                      currentSelected={task.assignee}
                      onChange={(userId) => handleAssigneeChange(task.id, userId)}
                    />
                  </Grid>
                )}
                {visibleCols.task && (
                  <Grid sx={{ flex: `0 0 ${colWidths.task}px` }}>
                    <TextField
                      fullWidth
                      variant="standard"
                      value={task.name || ''}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => task.id && handleTaskUpdate(task.id, 'name', e.target.value)}
            inputProps={{ style: { fontSize: 13 } }}
            sx={{ '& .MuiInputBase-input': { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' } }}
                      />
                    </Grid>
                  )}
                  {visibleCols.notes && (
                    <Grid sx={{ flex: `0 0 ${colWidths.notes}px` }}>
                        <Typography variant="body2" noWrap title={task.notes}>{task.notes || '-'}</Typography>
                    </Grid>
                  )}
                  {visibleCols.category && (
                  <Grid sx={{ flex: `0 0 ${colWidths.category}px` }}>
                    <Select
                      fullWidth
                      variant="standard"
                      displayEmpty
                      value={task.category || ''}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => task.id && handleTaskUpdate(task.id, 'category', e.target.value)}
                      sx={{ fontSize: 13 }}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {CATEGORY_OPTIONS.map((category) => (
                        <MenuItem key={category} value={category} sx={{ fontSize: 13 }}>{category}</MenuItem>
                      ))}
                    </Select>
                    </Grid>
                  )}
                  {visibleCols.task_class && (
                  <Grid sx={{ flex: `0 0 ${colWidths.task_class}px` }}>
                    <Select
                      fullWidth
                      variant="standard"
                      displayEmpty
                      value={task.task_class || ''}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => task.id && handleTaskUpdate(task.id, 'task_class', e.target.value)}
                      sx={{ fontSize: 13 }}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {CLASS_OPTIONS.map((cls) => (
                        <MenuItem key={cls} value={cls} sx={{ fontSize: 13 }}>{cls}</MenuItem>
                      ))}
                    </Select>
                    </Grid>
                  )}
                  {visibleCols.status && (
                  <Grid sx={{ flex: `0 0 ${colWidths.status}px` }}>
                      <StatusSelect
                        value={statusOptions.find(o => o.value.toLowerCase() === (task.status||'').toLowerCase())?.value || ''}
                        onChange={(e) => handleStatusChange(e, task.id)}
                        onClick={(e) => e.stopPropagation()}
                        displayEmpty
                        renderValue={(val) => <StatusBadge status={val || task.status || 'default'} />}
                      >
                        {statusOptions.map((option) => {
                          const meta = getStatusMeta(option.value);
                          return (
                            <MenuItem key={option.value} value={option.value} sx={{ gap: 1, fontSize: 13 }}>
                              <Box
                                component="span"
                                sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: meta.solid, flexShrink: 0 }}
                              />
                              {meta.short}
                            </MenuItem>
                          );
                        })}
                      </StatusSelect>
                    </Grid>
                  )}
                  {visibleCols.do_date && (
                    <Grid sx={{ flex: `0 0 ${colWidths.do_date}px` }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          format={DATE_FORMAT}
                          value={task.do_date ? dayjs(task.do_date) : null}
                          onChange={(newDate) =>
                            task.id &&
                            handleTaskUpdate(
                              task.id,
                              'do_date',
                              newDate ? dayjs(newDate).format('YYYY-MM-DD') : null
                            )
                          }
                          slots={{
                            textField: (params) => (
                              <TextField
                                {...params}
                                size="small"
                                fullWidth
                                variant="standard"
                                onClick={(e) => e.stopPropagation()}
                              />
                            ),
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </LocalizationProvider>
                    </Grid>
                  )}
                  {visibleCols.created_at && (
                    <Grid sx={{ flex: `0 0 ${colWidths.created_at}px` }}>
                      <Typography variant="body2" noWrap>
                        {task.created_at ? dayjs(task.created_at).format('YYYY-MM-DD') : '-'}
                      </Typography>
                    </Grid>
                  )}
                  {visibleCols.updated_at && (
                    <Grid sx={{ flex: `0 0 ${colWidths.updated_at}px` }}>
                      <Typography variant="body2" noWrap>
                        {task.updated_at ? dayjs(task.updated_at).format('YYYY-MM-DD') : '-'}
                      </Typography>
                    </Grid>
                  )}
                  {visibleCols.read && (
                    <Grid sx={{ flex: `0 0 ${colWidths.read}px` }}>
                      <Typography variant="body2" noWrap>{task.read ? 'Yes' : 'No'}</Typography>
                    </Grid>
                  )}
                  {visibleCols.is_archived && (
                    <Grid sx={{ flex: `0 0 ${colWidths.is_archived}px` }}>
                      <Typography variant="body2" noWrap>{task.is_archived ? 'Yes' : 'No'}</Typography>
                    </Grid>
                  )}
                    {visibleCols.reviewer && (
                      <Grid sx={{ flex: `0 0 ${colWidths.reviewer}px` }}>
                        <AssigneeDropdown
                          currentSelected={task.reviewer}
                          onChange={(userId) => handleReviewerChange(task.id, userId)}
                        />
                      </Grid>
                    )}
                  {visibleCols.line_up && (
                    <Grid sx={{ flex: `0 0 ${colWidths.line_up}px` }}>
                      <TextField
                        variant="standard"
                        type="number"
                        value={task.line_up || ''}
                        fullWidth
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => task.id && handleTaskUpdate(task.id, 'line_up', e.target.value)}
                        inputProps={{ style: { fontSize: 13 } }}
                      />
                    </Grid>
                  )}
                  {visibleCols.project && (
                    <Grid sx={{ flex: `0 0 ${colWidths.project}px` }}><Typography variant="body2" noWrap>{task.project || '-'}</Typography></Grid>
                  )}
                  {visibleCols.epic && (
                    <Grid sx={{ flex: `0 0 ${colWidths.epic}px` }}><Typography variant="body2" noWrap>{task.epic || task.primary_epic || '-'}</Typography></Grid>
                  )}
                   {visibleCols.primary_epic && (
                    <Grid sx={{ flex: `0 0 ${colWidths.primary_epic}px` }}><Typography variant="body2" noWrap>{task.primary_epic || '-'}</Typography></Grid>
                  )}
                  {visibleCols.track && (
                    <Grid sx={{ flex: `0 0 ${colWidths.track}px` }}><Typography variant="body2" noWrap>{task.track || '-'}</Typography></Grid>
                  )}
                  {visibleCols.sprint && (
                    <Grid sx={{ flex: `0 0 ${colWidths.sprint}px` }}><Typography variant="body2" noWrap>{SLOT_LABELS[task.sprint] || task.sprint || '-'}</Typography></Grid>
                  )}
                  {visibleCols.sprint_track_name && (
                    <Grid sx={{ flex: `0 0 ${colWidths.sprint_track_name}px` }}><Typography variant="body2" noWrap>{task.sprint_track_name || '-'}</Typography></Grid>
                  )}
                  {visibleCols.estimated_time && (
                    <Grid sx={{ flex: `0 0 ${colWidths.estimated_time}px` }}><Typography variant="body2">{est.toFixed(2)}</Typography></Grid>
                  )}
                  {visibleCols.total_time_taken && (
                    <Grid sx={{ flex: `0 0 ${colWidths.total_time_taken}px` }}><Typography variant="body2">{taken.toFixed(2)}</Typography></Grid>
                  )}
                  {visibleCols.action_items_count && (
                    <Grid sx={{ flex: `0 0 ${colWidths.action_items_count}px` }}><Typography variant="body2">{task.action_items_count || 0}</Typography></Grid>
                  )}
                    {visibleCols.completed_action_items_count && (
                    <Grid sx={{ flex: `0 0 ${colWidths.completed_action_items_count}px` }}><Typography variant="body2">{task.completed_action_items_count || 0}</Typography></Grid>
                  )}
                  {visibleCols.ai_progress && (
                    <Grid sx={{ flex: `0 0 ${colWidths.ai_progress}px` }}><Typography variant="body2" noWrap>{aiDone}/{aiTotal} ({aiPct}%)</Typography></Grid>
                  )}
                </TaskRow>
              );
            })}
          </Box>
        </Box>
        <Menu
            anchorReference="anchorPosition"
            anchorPosition={
                contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
            }
            open={contextMenu !== null}
            onClose={handleCloseContextMenu}
        >
            <MenuItem onClick={handleDuplicateTask}>
                Duplicate Task
            </MenuItem>
            <MenuItem
                onClick={handleDeleteTask}
                sx={{ color: 'error.main', '&:hover': { bgcolor: 'error.light' } }}
            >
                Delete Task
            </MenuItem>
        </Menu>
        {duplicateDialog}
    </Box>
);
};

export default ExtendedTaskList;
